import { useCallback, useEffect, useState } from 'react';
import { can, faNum, formatPercent, formatRial } from '@kia/shared';
import { api, errMsg } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { parseMonthLevers, type MonthLeverField } from '../lib/assumptionLevers';
import { Button, Card, EntityPicker, Field, Input, Kpi, Money, NumberInput, useToast } from '../ui';

// Persian labels for the four month levers — used only to name the offending field when a save aborts.
const LEVER_LABELS: Record<MonthLeverField, string> = {
  freightPct: 'نرخ حمل ورودی این ماه',
  floor: 'کف قیمت‌گذاری این ماه',
  target: 'هدف حاشیهٔ سود این ماه',
  floatPct: 'ضریب شناور ضایعات',
};

/**
 * Assumption Master (WS-A C5) — set the month's manufacturing **conversion pool** (direct labour + factory
 * overhead) and the **planned production mix** that spreads it across motors by winding weight (the eqKg
 * model, domain/absorption.ts). Admin/Finance-gated. The resulting absorption rate + freight are shown live.
 * With no pool/mix the rate is 0 and COGS is just material + freight — nothing is imposed silently.
 * Reads/writes GET/POST /api/assumptions/month; searches /api/products for the planned mix.
 */
interface MonthResponse {
  ym: string;
  row: {
    conversionPool: number;
    plannedOutput: Record<string, number>;
    fallbackKgPerMillionRial: number;
    note: string;
    freightPct: number | null;
    floor: number | null;
    target: number | null;
    floatPct: number;
    isLocked: boolean;
    version: number;
    /** 'gl_actual' when the saved pool equals the ledger's arrow-G suggestion, else 'manual'. */
    poolSource: string;
    suggestedPool: number | null;
  } | null;
  params: {
    freightPct: number;
    conversionRate: number;
    fallbackKgPerMillionRial: number;
    floor: number;
    target: number;
    driver: {
      basis: string;
      requested: string;
      trailingMonths: number;
      /** the PER-MONTH (normal-capacity) denominator the rate actually divided by */
      driverKg: number;
      /** the raw window total before the ÷monthsWithData normalisation */
      windowKg: number;
      /** months in the window that actually carried production (1 for the planned basis) */
      monthsWithData: number;
      productCount: number;
    };
  };
  loopSuggestion: {
    basisYm: string;
    suggestedPool: number;
    parts: { payroll: number; overhead: number; depreciation: number };
    empty: boolean;
  };
  history: { version: number; updatedBy: string; createdAt: string; conversionPool: number }[];
  /** rolling 3-month pool coverage: current (explicit) + next 2 (carried forward). */
  horizon: {
    ym: string;
    current: boolean;
    explicit: boolean;
    conversionPool: number;
    carriedFrom: string | null;
    healthy: boolean;
  }[];
}

// Where the absorption denominator's production mix comes from. 'produced' retired (one-basis rule).
const BASIS_LABELS: Record<string, string> = {
  planned: 'برنامهٔ دستی (تیراژ)',
  delivered: 'تحویل واقعی — میانگین متحرک',
  none: 'بدون مبنا',
};
interface ProductHit {
  code: string;
  name: string;
}
interface PlannedRow {
  code: string;
  name: string;
  units: number;
}

export function AssumptionMasterCard() {
  const { user } = useAuth();
  const toast = useToast();
  const canEdit = user ? can(user.role, 'config.write') : false;

  const [data, setData] = useState<MonthResponse | null>(null);
  const [pool, setPool] = useState(0);
  const [fallback, setFallback] = useState(0);
  const [planned, setPlanned] = useState<PlannedRow[]>([]);
  // Month lever OVERRIDES as percent strings ('' = not overridden ⇒ the global Settings lever applies).
  const [freightPctStr, setFreightPctStr] = useState('');
  const [floorPctStr, setFloorPctStr] = useState('');
  const [targetPctStr, setTargetPctStr] = useState('');
  const [floatPctStr, setFloatPctStr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    void api<MonthResponse>('/api/assumptions/month').then((r) => {
      if (!r.ok) return;
      const d = r.body as MonthResponse;
      setData(d);
      setPool(d.row?.conversionPool ?? 0);
      setFallback(d.row?.fallbackKgPerMillionRial ?? 0);
      setFreightPctStr(d.row?.freightPct == null ? '' : String(d.row.freightPct * 100));
      setFloorPctStr(d.row?.floor == null ? '' : String(d.row.floor * 100));
      setTargetPctStr(d.row?.target == null ? '' : String(d.row.target * 100));
      setFloatPctStr(d.row?.floatPct ? String(d.row.floatPct * 100) : '');
      setPlanned(
        Object.entries(d.row?.plannedOutput ?? {}).map(([code, units]) => ({
          code,
          name: code,
          units,
        })),
      );
    });
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  function addProduct(h: ProductHit) {
    if (!planned.some((p) => p.code === h.code)) {
      setPlanned([...planned, { code: h.code, name: h.name || h.code, units: 1 }]);
    }
  }

  async function save() {
    // Parse the four month levers BEFORE we touch the server. A non-empty field that parses to NaN (e.g. a
    // typo) must ABORT — never send null, which the server reads as "not overridden" and would silently
    // discard the operator's entry while reporting success (WEB-03). Empty stays null; range is the server's.
    const levers = parseMonthLevers({
      freightPct: freightPctStr,
      floor: floorPctStr,
      target: targetPctStr,
      floatPct: floatPctStr,
    });
    if (!levers.ok) {
      toast(
        `مقدار «${LEVER_LABELS[levers.field]}» نامعتبر است — عددی وارد کنید یا خالی بگذارید`,
        'error',
      );
      return;
    }
    setBusy(true);
    const plannedOutput: Record<string, number> = {};
    for (const p of planned) plannedOutput[p.code] = p.units;
    const r = await api<MonthResponse>('/api/assumptions/month', {
      method: 'POST',
      body: JSON.stringify({
        ym: data?.ym,
        conversionPool: pool,
        plannedOutput,
        fallbackKgPerMillionRial: fallback,
        freightPct: levers.overrides.freightPct,
        floor: levers.overrides.floor,
        target: levers.overrides.target,
        floatPct: levers.overrides.floatPct ?? 0,
        // Echo the version we loaded (E16-21) so a stale save is rejected, not silently clobbered.
        version: data?.row?.version,
      }),
    });
    setBusy(false);
    if (r.ok) {
      const d = r.body as MonthResponse;
      setData(d);
      toast('فرض ماهانه ذخیره شد');
    } else {
      const body = r.body as { error?: string; message?: string; current?: MonthResponse['row'] };
      // 409 conflict: someone else saved this month first — reload their values (never overwrite blindly).
      if (body?.error === 'version_conflict') load();
      toast(body?.message ?? 'خطا در ذخیره', 'error');
    }
  }

  async function toggleLock(locked: boolean) {
    const r = await api('/api/assumptions/month/lock', {
      method: 'POST',
      body: JSON.stringify({ ym: data?.ym, locked }),
    });
    if (!r.ok) {
      toast(errMsg(r), 'error');
      return;
    }
    toast(locked ? 'ماه قفل شد' : 'قفل ماه برداشته شد');
    load();
  }

  async function setBasis(basis: string) {
    const r = await api('/api/assumptions/driver-basis', {
      method: 'POST',
      body: JSON.stringify({ basis }),
    });
    if (!r.ok) {
      toast(errMsg(r), 'error');
      return;
    }
    toast('مبنای جذب به‌روزرسانی شد');
    load();
  }

  if (!data) {
    return (
      <Card title="فرض ماهانه (جذب دستمزد و سربار)">
        <p className="muted">در حال بارگذاری…</p>
      </Card>
    );
  }

  return (
    <Card title={`فرض ماهانه — جذب دستمزد و سربار (${data.ym})`}>
      <p className="muted" style={{ marginTop: 0 }}>
        هزینهٔ دستمزد مستقیم + سربار تولیدِ این ماه را وارد کنید و تیراژ برنامه‌ریزی‌شده را مشخص
        کنید؛ سامانه این هزینه را بر مبنای وزن سیم‌پیچ هر موتور در «بهای تمام‌شده» می‌نشاند. بدون
        تیراژ، نرخ جذب صفر است و بهای تمام‌شده فقط «مواد + حمل» می‌ماند. هزینهٔ عمومی و اداری اینجا
        نیست — از سود کسر می‌شود.
      </p>

      {(() => {
        // Lead the user to the two MANDATORY absorption inputs (R-A): without a pool AND at least one
        // product in the mix, the conversion rate is 0 and every motor's COGS is material+freight only.
        const needs: string[] = [];
        if (!(pool > 0)) needs.push('هزینهٔ تبدیل ماهانه');
        if (planned.length === 0) needs.push('حداقل یک محصول در تیراژ');
        return (
          <div
            className={needs.length ? 'banner banner-err' : 'banner banner-ok'}
            style={{ marginTop: 0 }}
          >
            {needs.length
              ? `برای «جذب دستمزد و سربار» لازم است: ${needs.join(' · ')} — تا آن، بهای تمام‌شده فقط «مواد + حمل» است و همهٔ حاشیه‌ها گمراه‌کننده‌اند.`
              : `ورودی‌های جذب کامل است ✓ — نرخ جذب: ${formatRial(data.params.conversionRate)} به ازای هر kg معادل.`}
          </div>
        );
      })()}

      {/* Rolling 3-month pool coverage: keep THIS month set (it's what the release gate enforces); the next
          two inherit it automatically. Set a future month only when its overhead will genuinely differ. */}
      <div style={{ marginTop: 12 }}>
        <div className="muted" style={{ fontSize: '.85em', marginBottom: 6 }}>
          پوشش سه‌ماههٔ نرخ جذب — ماه جاری را نگه دارید، دو ماه بعد به‌طور خودکار از آن ارث می‌برند
          (برای ماهی که هزینه‌اش فرق دارد، به همان ماه بروید و مقدارش را ثبت کنید):
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {data.horizon.map((m) => (
            <div
              key={m.ym}
              className={`banner ${m.healthy ? 'banner-ok' : 'banner-err'}`}
              style={{ margin: 0, padding: '6px 10px', fontSize: '.85em', minWidth: 128 }}
            >
              <b>{m.ym}</b>
              {m.current ? ' (جاری)' : ''}
              <div style={{ marginTop: 2 }}>
                {m.conversionPool > 0 ? formatRial(m.conversionPool) : '—'}
              </div>
              <div className="muted" style={{ fontSize: '.9em' }}>
                {m.explicit
                  ? 'ثبت‌شده'
                  : m.carriedFrom
                    ? `ارث از ${m.carriedFrom}`
                    : m.current
                      ? 'تنظیم‌نشده — لازم'
                      : 'تنظیم‌نشده'}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="form-grid">
        <Field label="هزینهٔ تبدیل ماهانه — دستمزد مستقیم + سربار تولید (﷼)" required>
          <NumberInput value={pool} onValue={setPool} disabled={!canEdit} />
          {data.row ? (
            <div style={{ marginTop: 6, fontSize: '.85em' }}>
              منبع مبلغ:{' '}
              <span
                style={{
                  color: data.row.poolSource === 'gl_actual' ? 'var(--green)' : 'var(--amber)',
                  fontWeight: 700,
                }}
              >
                {data.row.poolSource === 'gl_actual' ? 'از دفترکل ✓' : 'دستی'}
              </span>
              {!data.loopSuggestion.empty && pool !== data.loopSuggestion.suggestedPool ? (
                <span style={{ color: 'var(--ink-3)' }}>
                  {' '}
                  — با پیشنهاد دفترکل {formatRial(data.loopSuggestion.suggestedPool)} اختلاف دارد
                </span>
              ) : null}
            </div>
          ) : null}
        </Field>
        <Field
          label="ثابت جایگزین برای محصولات بدون سیم‌پیچ (kg بر میلیون ریال)"
          optional
          hint="۰ = مقدار پیشنهادی سامانه به‌کار می‌رود"
        >
          <NumberInput allowDecimal value={fallback} onValue={setFallback} disabled={!canEdit} />
        </Field>
        <Field
          label="نرخ حمل ورودی این ماه (٪)"
          optional
          hint="خالی = اهرم سراسری تنظیمات — این ماه مقدم است"
        >
          <Input
            value={freightPctStr}
            onChange={(e) => setFreightPctStr(e.target.value)}
            placeholder={`${(data.params.freightPct * 100).toFixed(2)}`}
            disabled={!canEdit}
          />
        </Field>
        <Field
          label="کف قیمت‌گذاری این ماه (٪)"
          optional
          hint="خالی = اهرم سراسری تنظیمات — سدهای ترخیص جدا هستند"
        >
          <Input
            value={floorPctStr}
            onChange={(e) => setFloorPctStr(e.target.value)}
            placeholder={`${(data.params.floor * 100).toFixed(0)}`}
            disabled={!canEdit}
          />
        </Field>
        <Field
          label="هدف حاشیهٔ سود این ماه (٪)"
          optional
          hint="قیمت پیشنهادی به این حد می‌رسد (نه کف). خالی/برابر کف = بدون تغییر تا وقتی هدف را بالاتر ببرید."
        >
          <Input
            value={targetPctStr}
            onChange={(e) => setTargetPctStr(e.target.value)}
            placeholder={`${(data.params.target * 100).toFixed(0)}`}
            disabled={!canEdit}
          />
        </Field>
        <Field
          label="ضریب شناور ضایعات (٪)"
          optional
          hint="فقط نام‌گذاری و دید تحلیلگر — در هیچ محاسبه‌ای نمی‌نشیند (D6: درآمد ضایعات پوشش است)"
        >
          <Input
            value={floatPctStr}
            onChange={(e) => setFloatPctStr(e.target.value)}
            placeholder="مثلاً ۵"
            disabled={!canEdit}
          />
        </Field>
      </div>

      {canEdit ? (
        <EntityPicker<ProductHit>
          label="افزودن محصول به تیراژ برنامه‌ریزی‌شده"
          endpoint="/api/products"
          maxResults={6}
          itemKey={(h) => h.code}
          placeholder="جستجوی محصول با نام یا کد…"
          renderResult={(h) => ({ label: h.name || h.code, subtitle: `(${h.code})` })}
          onSelect={addProduct}
        />
      ) : null}

      {planned.length > 0 ? (
        <table className="table" style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>محصول</th>
              <th style={{ width: 140 }}>
                تعداد برنامه{' '}
                <span className="field-req" title="الزامی">
                  *
                </span>
              </th>
              <th style={{ width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {planned.map((p, i) => (
              <tr key={p.code}>
                <td>
                  {p.name} <span className="muted">({p.code})</span>
                </td>
                <td>
                  <NumberInput
                    value={p.units}
                    disabled={!canEdit}
                    onValue={(n) =>
                      setPlanned(planned.map((x, j) => (j === i ? { ...x, units: n } : x)))
                    }
                  />
                </td>
                <td>
                  {canEdit ? (
                    <Button
                      variant="ghost"
                      onClick={() => setPlanned(planned.filter((_, j) => j !== i))}
                    >
                      حذف
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="muted">هنوز محصولی برای تیراژ افزوده نشده — نرخ جذب صفر است.</p>
      )}

      <div className="kpi-grid" style={{ marginTop: 12 }}>
        <Kpi
          label="نرخ جذب (ریال بر kg معادل)"
          value={<Money value={data.params.conversionRate} />}
        />
        <Kpi
          label="نرخ حمل ورودی"
          value={formatPercent(data.params.freightPct, 2)}
          title="ویرایش در همین صفحه — کارت اهرم‌ها، «نرخ حمل ورودی»"
        />
      </div>

      {/* R5 — where the denominator comes from: manual plan vs real production (trailing normal-capacity). */}
      <div style={{ marginTop: 12 }}>
        {canEdit ? (
          <Field
            label="مبنای مخرج جذب"
            hint="پیش‌فرض «تحویل واقعی» است (قاعدهٔ یک‌مبنا: همه‌چیز بر واحدهای تحویل‌شده)؛ بدون سابقهٔ تحویل به برنامهٔ دستی بازمی‌گردد. طول پنجره در تنظیمات."
          >
            <select
              value={data.params.driver.requested}
              onChange={(e) => void setBasis(e.target.value)}
              style={{
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid var(--line)',
                background: 'var(--bg)',
                color: 'var(--text)',
              }}
            >
              <option value="delivered">{BASIS_LABELS.delivered}</option>
              <option value="planned">{BASIS_LABELS.planned}</option>
            </select>
          </Field>
        ) : null}
        <p className="muted" style={{ marginTop: 6 }}>
          مبنای استفاده‌شده:{' '}
          <b>{BASIS_LABELS[data.params.driver.basis] ?? data.params.driver.basis}</b>
          {data.params.driver.requested !== data.params.driver.basis
            ? ` — «${BASIS_LABELS[data.params.driver.requested]}» انتخاب شده اما در پنجرهٔ ${data.params.driver.trailingMonths} ماهه داده‌ای نبود، به برنامه بازگشت`
            : ''}
          {' · '}مخرج: <span className="num">{faNum(Math.round(data.params.driver.driverKg))}</span>{' '}
          kg معادل <b>در ماه</b>{' '}
          {data.params.driver.basis === 'planned'
            ? `(برنامهٔ ماه · ${faNum(data.params.driver.productCount)} محصول)`
            : `(میانگین ${faNum(data.params.driver.monthsWithData)} ماه تولید در پنجرهٔ ${faNum(
                data.params.driver.trailingMonths,
              )} ماهه · ${faNum(data.params.driver.productCount)} محصول)`}
        </p>
      </div>

      {/* Arrow G — the loop's advice: what the LEDGER says last month's conversion actually cost. */}
      {!data.loopSuggestion.empty ? (
        <p className="muted" style={{ marginTop: 10 }}>
          پیشنهاد حلقه از دفاتر ماه {data.loopSuggestion.basisYm}:{' '}
          <b>{formatRial(data.loopSuggestion.suggestedPool)}</b>{' '}
          <span className="muted">
            (حقوق {formatRial(data.loopSuggestion.parts.payroll)} · سربار{' '}
            {formatRial(data.loopSuggestion.parts.overhead)} · استهلاک{' '}
            {formatRial(data.loopSuggestion.parts.depreciation)})
          </span>{' '}
          — فقط پیشنهاد؛ هرگز خودکار اعمال نمی‌شود.
          {canEdit && !data.row?.isLocked ? (
            <Button variant="ghost" onClick={() => setPool(data.loopSuggestion.suggestedPool)}>
              در فرم بگذار
            </Button>
          ) : null}
        </p>
      ) : null}

      {data.history.length > 1 ? (
        <p className="muted" style={{ marginTop: 4 }}>
          تاریخچه: {data.history.length} نسخه — آخرین‌ها:{' '}
          {data.history
            .slice(0, 3)
            .map((h) => `v${h.version} (${formatRial(h.conversionPool)}، ${h.updatedBy})`)
            .join('، ')}
        </p>
      ) : null}

      {canEdit ? (
        <div className="row" style={{ marginTop: 12 }}>
          <Button onClick={save} loading={busy} disabled={!!data.row?.isLocked}>
            ذخیرهٔ فرض ماهانه
          </Button>
          {data.row ? (
            <Button variant="ghost" onClick={() => void toggleLock(!data.row!.isLocked)}>
              {data.row.isLocked ? 'برداشتن قفل ماه' : 'قفل کردن ماه'}
            </Button>
          ) : null}
          {data.row?.isLocked ? (
            <span className="muted">🔒 ماه قفل است — فرض‌ها و بستن ماه تغییرناپذیرند.</span>
          ) : null}
        </div>
      ) : (
        <p className="muted">فقط مدیر می‌تواند فرض ماهانه را تغییر دهد.</p>
      )}
    </Card>
  );
}
